import { IconChevronDown, IconChevronRight, IconSparkles } from '@tabler/icons-react'
import { ResourceTypeBadge } from './ResourceTypeBadge'
import type { ResourceTypeCount } from '@/hooks'

type Props = {
  displayDate: string
  totalCount: number
  resourceCounts: ResourceTypeCount[]
  isExpanded: boolean
  onToggle: () => void
  // Optional - for Patient chart view (single patient)
  summary?: string | null
  summaryLoading?: boolean
  summaryError?: string | null
  // Optional - for Home view (multi-patient)
  patientCount?: number
}

export function DateGroupHeader({
  displayDate,
  totalCount,
  resourceCounts,
  isExpanded,
  onToggle,
  summary,
  summaryLoading,
  summaryError,
  patientCount,
}: Props) {
  // Filter out resource counts that don't have valid icons to prevent render errors
  const validResourceCounts = resourceCounts.filter((rc) => rc.icon)
  const showSummary = summaryLoading || summary || summaryError

  return (
    <div
      className="sticky top-0 z-10 cursor-pointer border-b border-b-[#D0E1F2] bg-[#F5F8FC] transition-colors hover:bg-[#EBF2FA]"
      onClick={onToggle}
    >
      <div className="flex items-center gap-2 px-4 py-2">
        {isExpanded ? (
          <IconChevronDown size={16} className="flex-shrink-0 text-gray-500" />
        ) : (
          <IconChevronRight size={16} className="flex-shrink-0 text-gray-500" />
        )}

        <span className="text-sm font-semibold text-[#313131]">{displayDate}</span>

        {patientCount !== undefined && (
          <span className="text-xs text-[#7D7987]">
            ({patientCount} {patientCount === 1 ? 'patient' : 'patients'})
          </span>
        )}

        <span className="ml-auto flex-shrink-0 text-xs text-[#7D7987]">
          {totalCount} {totalCount === 1 ? 'item' : 'items'}
        </span>
      </div>

      {/* AI Summary - shown when expanded */}
      {isExpanded && showSummary && (
        <div className="px-4 pb-1 pl-8 text-xs" style={{ wordBreak: 'break-word' }}>
          <IconSparkles size={12} className="mr-1 inline-block align-middle text-purple-500" />
          {summaryLoading ? (
            <span className="animate-pulse text-gray-400 italic">Generating summary...</span>
          ) : summaryError ? (
            <span className="text-red-400 italic">{summaryError}</span>
          ) : summary ? (
            <span className="text-gray-600 italic">"{summary}"</span>
          ) : null}
        </div>
      )}

      {/* Resource type badges */}
      <div className="flex flex-wrap gap-1 px-4 pb-2 pl-8">
        {validResourceCounts.slice(0, 4).map((rc) => (
          <ResourceTypeBadge
            key={rc.dataKey}
            count={rc.count}
            icon={rc.icon}
            color={rc.color}
            dataKey={rc.dataKey}
          />
        ))}
        {validResourceCounts.length > 4 && (
          <span className="self-center text-xs text-[#7D7987]">
            +{validResourceCounts.length - 4} more
          </span>
        )}
      </div>
    </div>
  )
}
